import { Dispatch, FC, PropsWithChildren, SetStateAction, useCallback, useState } from 'react';
import { OptionsMenu, Tab, Typography, SelectMenu, Icon } from '@scale/scaleui';
import { useWorkerCommentsStore } from '../hooks/useWorkerCommentsStore';
import { useAttemptBehaviorLog } from '../contexts/AttemptBehaviorLogContext';
import { useChatStoreExtract, useChatStoreRef } from '../../Chat/hooks/ChatStore.hooks';
import { IndexedInteractionStepResponse } from '@scale/shared/types/chat';
import { LoadFromTaskModal } from './LoadFromTaskModal';
import { ReviewLevel } from '@scale/shared-types';
import { twMerge } from 'tailwind-merge';
import { useChatDemoStore } from './chatDemo.store';
import { useChatThemeStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';
import { BrandLogo } from '@scale/scaleui-radix';
import { ChatEventBus } from '../events';

type InteractionDemoInterfaceProps = PropsWithChildren<{
  getEventBus: () => ChatEventBus | undefined;
}>;

const TABS = {
  'Task Config': 0,
  'Task Preview': 1,
  'Task Audit': 2,
  'Response Templating': 3,
} as const;

const Options = ({
  setLoadFromTaskModalOpen,
  getEventBus,
}: {
  setLoadFromTaskModalOpen: Dispatch<SetStateAction<boolean>>;
  getEventBus: () => ChatEventBus | undefined;
}) => {
  const chatStoreRef = useChatStoreRef();

  const [redoHistory, setRedoHistory] = useState<IndexedInteractionStepResponse[]>([]);

  const responses = useChatStoreExtract(state => state.responses);
  const setWorkerComments = useWorkerCommentsStore(state => state.setWorkerComments);

  const resetResponses = useCallback(() => {
    const eventBus = getEventBus();
    if (!eventBus) return;
    eventBus.emit('chat:reset', {});
  }, [getEventBus]);
  const { getAttemptBehaviourLog } = useAttemptBehaviorLog();

  const undo = useCallback(() => {
    const currentChatStoreState = chatStoreRef.current.getState();
    const responses = currentChatStoreState.responses;
    const setResponses = currentChatStoreState.setResponses;

    if (responses.length === 0) {
      return;
    }
    const newResponses = responses.slice(0, responses.length - 1);
    const newHistoryItem = responses[responses.length - 1];
    setResponses(newResponses);
    setRedoHistory(history => [newHistoryItem, ...history]);
  }, [chatStoreRef]);

  const redo = useCallback(() => {
    const currentChatStoreState = chatStoreRef.current.getState();
    const responses = currentChatStoreState.responses;
    const setResponses = currentChatStoreState.setResponses;

    if (redoHistory.length === 0) {
      return;
    }
    const newResponses = [...responses, redoHistory[0]];
    setResponses(newResponses);
    setRedoHistory(history => history.slice(1));
  }, [chatStoreRef, redoHistory]);

  const saveConfiguration = useCallback(() => {
    const currentChatStoreState = chatStoreRef.current.getState();
    const responses = currentChatStoreState.responses;
    const config = currentChatStoreState.config;

    localStorage.setItem(
      'multi-turn-save-state',
      JSON.stringify({
        config,
        responses,
      }),
    );
  }, [chatStoreRef]);

  const deleteSavedConfiguration = useCallback(() => {
    localStorage.removeItem('multi-turn-save-state');
  }, []);

  return (
    <OptionsMenu
      direction="bottom-right"
      menuItems={[
        {
          icon: ['far', 'undo'],
          id: 'undo',
          label: 'Undo last step',
          onClick: undo,
          disabled: responses.length === 0,
        },
        {
          icon: ['far', 'redo'],
          id: 'redo',
          label: 'Redo recent step',
          onClick: redo,
          disabled: redoHistory.length === 0,
        },
        {
          icon: ['far', 'trash-alt'],
          id: 'reset',
          label: 'Reset all responses',
          onClick: resetResponses,
        },
        {
          icon: ['far', 'comment-times'],
          id: 'clear-commments',
          label: 'Clear all comments',
          onClick: () => {
            setWorkerComments([]);
          },
        },
        {
          icon: ['far', 'snowboarding'],
          id: 'behavior-log',
          label: 'Check behavior log',
          onClick: () => {
            // eslint-disable-next-line no-console
            console.log(JSON.stringify(getAttemptBehaviourLog(), null, 2));
          },
        },
        {
          icon: ['far', 'save'],
          id: 'save-state',
          label: 'Save current task state',
          onClick: saveConfiguration,
        },
        {
          icon: 'unlink',
          id: 'delete-state',
          label: 'Clear saved task state',
          onClick: deleteSavedConfiguration,
        },
        {
          icon: 'link',
          id: 'load-from-task',
          label: 'Load config from existing task',
          onClick: () => setLoadFromTaskModalOpen(true),
        },
      ]}
    />
  );
};

export const InteractionDemoInterface: FC<InteractionDemoInterfaceProps> = ({
  children,
  getEventBus,
}) => {
  const [loadFromTaskModalOpen, setLoadFromTaskModalOpen] = useState<boolean>(false);

  const setResponseViewerOpen = useChatDemoStore(state => state.setResponseViewerOpen);
  const isResponseViewerOpen = useChatDemoStore(state => state.isResponseViewerOpen);

  const setResponses = useChatStoreExtract(state => state.setResponses);
  const setConfig = useChatStoreExtract(state => state.setConfig);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const setReviewLevel = useChatStoreExtract(state => state.setReviewLevel);

  const [activeTab, _setActiveTab] = useState<number>(TABS['Task Config']);
  const theme = useChatThemeStore(state => state.theme);

  const setActiveTab = useCallback(
    (activeTab: number) => {
      _setActiveTab(activeTab);
      setResponseViewerOpen(false);
    },
    [_setActiveTab, setResponseViewerOpen],
  );

  return (
    <>
      <LoadFromTaskModal
        open={loadFromTaskModalOpen}
        onClose={() => setLoadFromTaskModalOpen(false)}
        setResponses={setResponses}
        setConfig={setConfig}
      />
      <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
        <div className="w-full h-full scaleui relative">
          <div className="flex sticky top-0 z-50 justify-between h-14 w-full px-4 py-2 border-b bg-neutral-0 border-neutral-200">
            <div className="flex items-center space-x-2">
              <div style={{ height: 30, width: 30 }}>
                <BrandLogo size="4" />
              </div>
              <Typography.Subtitle2>Chat Demo</Typography.Subtitle2>

              <Options
                setLoadFromTaskModalOpen={setLoadFromTaskModalOpen}
                getEventBus={getEventBus}
              />

              {activeTab === TABS['Task Preview'] && (
                <div className="flex items-center gap-2 text-sm px-1">
                  <Typography.Subtitle3>View task as:</Typography.Subtitle3>
                  <SelectMenu
                    menuDirection="bottom"
                    options={[
                      {
                        id: ReviewLevel.Attempt as string,
                        name: 'Attempt',
                      },
                      {
                        id: ReviewLevel.Normal as string,
                        name: 'L0',
                      },
                      {
                        id: ReviewLevel.Level1 as string,
                        name: 'L1',
                      },
                      {
                        id: ReviewLevel.ReviewConsensus as string,
                        name: 'Consensus: L4',
                      },
                      {
                        id: ReviewLevel.Corp as string,
                        name: 'Final: L10',
                      },
                    ]}
                    setSelected={r => setReviewLevel(r as ReviewLevel)}
                    selectedOptionId={reviewLevel as string}
                  />
                  <Icon
                    icon="brackets-curly"
                    tooltip="JSON Response Viewer"
                    onClick={() => setResponseViewerOpen(!isResponseViewerOpen)}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center">
              <Tab.List className="px-12">
                {Object.keys(TABS).map((tabName: string) => (
                  <Tab data-testid={tabName} key={tabName}>
                    {tabName}
                  </Tab>
                ))}
              </Tab.List>
            </div>
          </div>
          <div
            className={twMerge(
              'flex justify-center w-full h-[calc(100%-3.5rem)] overflow-auto',
              activeTab === TABS['Task Preview'] ? theme : '',
            )}
          >
            <div className="w-full h-full relative bg-neutral-0">
              <Tab.Panels>{children}</Tab.Panels>
            </div>
          </div>
        </div>
      </Tab.Group>
    </>
  );
};
